/**
 * Plain-text diagnostic report designed to be PASTED verbatim.
 *
 * It arose from a specific case: the app remained "syncing" forever because
 * the PowerSync service was not even running. From the screen there was no way
 * to distinguish "does not connect" from "connects but is slow", and describing
 * it manually loses half the data.
 *
 * SINGLE SOURCE OF TRUTH. The first version received its own object while the
 * screen rendered its rows independently: two sources for the same thing, so
 * adding a row to the UI left the report incomplete without anyone noticing.
 * Now both consume the SAME sections array: the screen renders it, this function
 * serializes it, and the hint is computed by reading it.
 *
 * Each item carries three fields for different reasons:
 *   · `label`/`value` — already translated text: what is displayed and copied.
 *   · `status`        — used only by the UI for color; ignored by the report.
 *   · `raw`           — UNTRANSLATED value, so logic does not depend on
 *                       language. Without it, the hint would stop firing whenever
 *                       someone changed a translation.
 *
 * It does NOT include personal data: no email, name, or token. Whoever builds
 * the sections is responsible for redacting identifiers.
 */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Stable identifier, independent of language. Used by logic. */
  key: string;
  /** Already translated label. */
  label: string;
  /** Already translated / formatted value, exactly as displayed. */
  value: string;
  /** UI only. */
  status?: DiagnosticStatus;
  /** Raw value for logic that must not depend on language. */
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** ISO timestamp. Passed in; the clock is not read here (pure function). */
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Searches by `key`, never by label: labels are translated. */
function findItem(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const found = s.items.find((i) => i.key === key);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Actionable hint for the pattern that started all this: there is a session and
 * synchronization does not start. It is almost always a downed service, not the
 * app, and saying so here saves searching in the wrong place.
 */
function hint(sections: DiagnosticSection[]): string[] {
  const sessionItem = findItem(sections, "session");
  const sync = findItem(sections, "sync");
  if (
    sessionItem?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Hint",
    `The session is active but PowerSync does not reach "connected" (${String(sync.raw)}).`,
    "Check that the PowerSync service responds at the URL above: if it gives",
    "502 or does not respond, the problem is the stack, not the app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const parts: string[] = [`# Diagnostics — ${input.appName}`, input.timestamp];

  for (const section of input.sections) {
    parts.push("", `## ${section.title}`);
    for (const item of section.items) {
      // An empty field is MARKED rather than omitted: in a pasted report, a
      // missing datum and an uncollected one read the same unless we say which.
      parts.push(`- ${item.label}: ${item.value === "" ? "(no data)" : item.value}`);
    }
  }

  parts.push(...hint(input.sections));
  return parts.join("\n");
}
